import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Edit3,
  Trash2,
  Save,
  X,
  Search,
  RefreshCw,
  FileX,
  AlertCircle,
  CheckCircle,
  DollarSign,
  Calendar,
  MessageSquare
} from 'lucide-react';
import { initializeApp, getApps } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Service class for interacting with the backend (reuses existing lookup table functions)
class IgnoreListServiceClass {
  constructor() {
    this.functions = null;
    this.initialized = false;
    this.tableName = 'commercial_invoice_ignore_list';
  }

  async initialize() {
    if (this.initialized) return;
    try {
      if (getApps().length === 0) {
        const firebaseConfig = typeof window.__firebase_config !== 'undefined' ? JSON.parse(window.__firebase_config) : {};
        initializeApp(firebaseConfig);
      }
      this.functions = getFunctions();
      this.initialized = true;
      console.log('IgnoreListService initialized with Cloud Functions');
    } catch (error) {
      console.error('Failed to initialize IgnoreListService:', error);
      throw error;
    }
  }

  async getIgnoreList() {
    await this.initialize();
    try {
      const getLookupTableData = httpsCallable(this.functions, 'getLookupTableData');
      const result = await getLookupTableData({ tableName: this.tableName });
      return result.data.data || [];
    } catch (error) {
      console.error('Error getting ignore list:', error);
      throw new Error(`Failed to get ignore list: ${error.message}`);
    }
  }

  async addToIgnoreList(record) {
    await this.initialize();
    try {
      const addLookupRecord = httpsCallable(this.functions, 'addLookupRecord');
      const result = await addLookupRecord({ tableName: this.tableName, record });
      return result.data.data;
    } catch (error) {
      console.error('Error adding to ignore list:', error);
      throw new Error(`Failed to add to ignore list: ${error.message}`, { cause: error.details });
    }
  }

  async updateIgnoreListRecord(id, record) {
    await this.initialize();
    try {
      const updateLookupRecord = httpsCallable(this.functions, 'updateLookupRecord');
      const result = await updateLookupRecord({ tableName: this.tableName, id, record });
      return result.data.data.updatedRecord;
    } catch (error) {
      console.error('Error updating ignore list record:', error);
      throw new Error(`Failed to update record: ${error.message}`, { cause: error.details });
    }
  }

  async removeFromIgnoreList(id) {
    await this.initialize();
    try {
      const deleteLookupRecord = httpsCallable(this.functions, 'deleteLookupRecord');
      const result = await deleteLookupRecord({ tableName: this.tableName, id });
      return result.data.data.success;
    } catch (error) {
      console.error('Error removing from ignore list:', error);
      throw new Error(`Failed to remove from ignore list: ${error.message}`, { cause: error.details });
    }
  }
}

const IgnoreListService = new IgnoreListServiceClass();

// Field definitions for the ignore list form
const IGNORE_LIST_FIELDS = [
  {
    key: 'commercial_invoice',
    label: 'Commercial Invoice #',
    type: 'text',
    required: true,
    placeholder: 'Enter commercial invoice number (e.g., INV-2024-001)',
    description: 'The exact commercial invoice number to exclude from cost calculations'
  },
  {
    key: 'reason',
    label: 'Reason for Ignoring',
    type: 'select',
    required: true,
    options: [
      { value: 'duplicate', label: 'Duplicate Entry' },
      { value: 'cancelled', label: 'Cancelled Order' },
      { value: 'test_data', label: 'Test Data' },
      { value: 'data_error', label: 'Data Entry Error' },
      { value: 'returned', label: 'Returned Shipment' },
      { value: 'sample_shipment', label: 'Sample Shipment' },
      { value: 'internal_transfer', label: 'Internal Transfer' },
      { value: 'other', label: 'Other' }
    ],
    placeholder: 'Select a reason'
  },
  {
    key: 'notes',
    label: 'Additional Notes',
    type: 'textarea',
    required: false,
    placeholder: 'Add any additional details or context for why this invoice is being ignored...',
    description: 'Optional notes for future reference'
  },
  {
    key: 'added_by',
    label: 'Added By',
    type: 'text',
    required: false,
    placeholder: 'Your name or ID',
    description: 'Person who added this entry'
  },
  {
    key: 'active',
    label: 'Active',
    type: 'boolean',
    required: false,
    description: 'Uncheck to temporarily stop ignoring this invoice without deleting the record'
  }
];

function IgnoreListForm({ record, onSave, onCancel, isEditing }) {
  const [formData, setFormData] = useState(record || {});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      const initialData = { active: true };
      IGNORE_LIST_FIELDS.forEach(field => {
        if (field.type === 'boolean') {
          initialData[field.key] = true;
        }
      });
      setFormData(prev => ({ ...initialData, ...prev }));
    }
  }, [isEditing]);

  const validateForm = () => {
    const newErrors = {};
    IGNORE_LIST_FIELDS.forEach(field => {
      if (field.required && !formData[field.key]) {
        newErrors[field.key] = `${field.label} is required`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      setSaving(true);
      try {
        await onSave(formData);
      } finally {
        setSaving(false);
      }
    }
  };

  const handleInputChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: null }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-amber-50 to-orange-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <FileX className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {isEditing ? 'Edit Ignore List Entry' : 'Add to Ignore List'}
              </h3>
              <p className="text-sm text-gray-600">
                {isEditing
                  ? 'Update the details for this ignored invoice'
                  : 'Add a commercial invoice to be excluded from cost calculations'}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {IGNORE_LIST_FIELDS.map(field => (
            <div key={field.key}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              {field.description && (
                <p className="text-xs text-gray-500 mb-2">{field.description}</p>
              )}

              {field.type === 'text' && (
                <input
                  type="text"
                  value={formData[field.key] || ''}
                  onChange={(e) => handleInputChange(field.key, e.target.value)}
                  placeholder={field.placeholder}
                  disabled={saving}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent ${
                    errors[field.key] ? 'border-red-500' : 'border-gray-300'
                  } ${saving ? 'bg-gray-50' : ''}`}
                />
              )}

              {field.type === 'select' && (
                <select
                  value={formData[field.key] || ''}
                  onChange={(e) => handleInputChange(field.key, e.target.value)}
                  disabled={saving}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent ${
                    errors[field.key] ? 'border-red-500' : 'border-gray-300'
                  } ${saving ? 'bg-gray-50' : ''}`}
                >
                  <option value="">{field.placeholder}</option>
                  {field.options.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              )}

              {field.type === 'textarea' && (
                <textarea
                  value={formData[field.key] || ''}
                  onChange={(e) => handleInputChange(field.key, e.target.value)}
                  placeholder={field.placeholder}
                  rows={3}
                  disabled={saving}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent ${
                    errors[field.key] ? 'border-red-500' : 'border-gray-300'
                  } ${saving ? 'bg-gray-50' : ''}`}
                />
              )}

              {field.type === 'boolean' && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={!!formData[field.key]}
                    onChange={(e) => handleInputChange(field.key, e.target.checked)}
                    disabled={saving}
                    className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-600">
                    {formData[field.key] ? 'Currently active (invoice is being ignored)' : 'Inactive (invoice is NOT being ignored)'}
                  </span>
                </div>
              )}

              {errors[field.key] && (
                <p className="text-sm text-red-500 mt-1">{errors[field.key]}</p>
              )}
            </div>
          ))}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <X className="h-4 w-4 inline mr-1" />
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 border border-transparent rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center"
            >
              {saving ? (
                <RefreshCw className="h-4 w-4 inline mr-1 animate-spin" />
              ) : (
                <Save className="h-4 w-4 inline mr-1" />
              )}
              {saving ? 'Saving...' : (isEditing ? 'Update' : 'Add to Ignore List')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Reason label mapping for display
const REASON_LABELS = {
  duplicate: 'Duplicate Entry',
  cancelled: 'Cancelled Order',
  test_data: 'Test Data',
  data_error: 'Data Entry Error',
  returned: 'Returned Shipment',
  sample_shipment: 'Sample Shipment',
  internal_transfer: 'Internal Transfer',
  other: 'Other'
};

// Reason color mapping
const REASON_COLORS = {
  duplicate: 'bg-yellow-100 text-yellow-800',
  cancelled: 'bg-red-100 text-red-800',
  test_data: 'bg-purple-100 text-purple-800',
  data_error: 'bg-orange-100 text-orange-800',
  returned: 'bg-blue-100 text-blue-800',
  sample_shipment: 'bg-green-100 text-green-800',
  internal_transfer: 'bg-gray-100 text-gray-800',
  other: 'bg-slate-100 text-slate-800'
};

export default function ActualCostSettings() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const ignoreList = await IgnoreListService.getIgnoreList();
      setData(Array.isArray(ignoreList) ? ignoreList : []);
    } catch (err) {
      console.error('Error loading ignore list:', err);
      setError(`Failed to load ignore list: ${err.message}`);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleAdd = () => {
    setEditingRecord(null);
    setShowForm(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setShowForm(true);
  };

  const handleSave = async (formData) => {
    setError(null);
    try {
      if (editingRecord) {
        const updated = await IgnoreListService.updateIgnoreListRecord(
          editingRecord.id,
          formData
        );
        setData(prev => prev.map(item =>
          item.id === updated.id ? { ...item, ...updated } : item
        ));
        setSuccessMessage(`Successfully updated "${formData.commercial_invoice}" in ignore list`);
      } else {
        const created = await IgnoreListService.addToIgnoreList(formData);
        setData(prev => [created, ...prev]);
        setSuccessMessage(`Successfully added "${formData.commercial_invoice}" to ignore list`);
      }
      setShowForm(false);
      setEditingRecord(null);
    } catch (err) {
      console.error('Error saving record:', err);
      const errorMessage = err.message || '';
      if (errorMessage.includes('streaming buffer')) {
        setError(`Cannot ${editingRecord ? 'update' : 'add'}: Please wait 90-120 minutes for BigQuery's streaming buffer to clear.`);
      } else {
        setError(`Failed to save record: ${err.message}`);
      }
    }
  };

  const handleDelete = async (record) => {
    setError(null);
    try {
      await IgnoreListService.removeFromIgnoreList(record.id);
      setData(prev => prev.filter(item => item.id !== record.id));
      setDeleteConfirm(null);
      setSuccessMessage(`Successfully removed "${record.commercial_invoice}" from ignore list`);
    } catch (err) {
      console.error('Error deleting record:', err);
      const errorMessage = err.message || '';
      if (errorMessage.includes('streaming buffer')) {
        setError(`Cannot delete: Please wait 90-120 minutes for BigQuery's streaming buffer to clear.`);
      } else {
        setError(`Failed to delete record: ${err.message}`);
      }
    }
  };

  const filteredData = Array.isArray(data)
    ? data.filter(record => record && typeof record === 'object').filter(record =>
        Object.values(record).some(value =>
          value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    : [];

  const activeCount = filteredData.filter(r => r.active !== false).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 text-amber-600 animate-spin" />
        <span className="ml-2 text-gray-600">Loading Actual Cost Settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200 p-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-amber-100 rounded-xl">
            <DollarSign className="h-8 w-8 text-amber-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Actual Cost Settings</h1>
            <p className="text-gray-600">
              Manage which commercial invoices should be excluded from cost allocation calculations
            </p>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
            <span className="text-green-700">{successMessage}</span>
            <button
              onClick={() => setSuccessMessage(null)}
              className="ml-auto text-green-600 hover:text-green-800"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
            <div className="flex-grow">
              <span className="font-semibold text-red-800">Error:</span>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-600 hover:text-red-800 flex-shrink-0"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Ignore List Section */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileX className="h-5 w-5 text-amber-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Commercial Invoice Ignore List</h2>
                <p className="text-sm text-gray-600">
                  Invoices in this list will be excluded from the cost allocation view
                </p>
              </div>
            </div>
            <button
              onClick={handleAdd}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Add Invoice
            </button>
          </div>
        </div>

        {/* Search and Stats */}
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search by invoice number, reason, or notes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <span className="px-3 py-1 bg-white rounded-full border">
                Total: <span className="font-semibold">{filteredData.length}</span>
              </span>
              <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full border border-amber-200">
                Active: <span className="font-semibold">{activeCount}</span>
              </span>
              <button
                onClick={loadData}
                className="flex items-center gap-1 px-3 py-1 text-amber-600 hover:bg-amber-50 rounded border border-amber-200"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Commercial Invoice
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reason
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Notes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Added By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date Added
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredData.map((record) => (
                <tr key={record.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <FileX className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm font-medium text-gray-900">
                        {record.commercial_invoice || '-'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${REASON_COLORS[record.reason] || 'bg-gray-100 text-gray-800'}`}>
                      {REASON_LABELS[record.reason] || record.reason || '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="max-w-xs truncate text-sm text-gray-600" title={record.notes}>
                      {record.notes ? (
                        <div className="flex items-center">
                          <MessageSquare className="h-3 w-3 text-gray-400 mr-1 flex-shrink-0" />
                          {record.notes}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {record.added_by || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {record.created_at ? (
                      <div className="flex items-center">
                        <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                        {new Date(record.created_at).toLocaleDateString()}
                      </div>
                    ) : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      record.active !== false
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {record.active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEdit(record)}
                        className="text-amber-600 hover:text-amber-900 p-1 hover:bg-amber-50 rounded"
                        title="Edit"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(record)}
                        className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {filteredData.length === 0 && (
          <div className="text-center py-12">
            <FileX className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No ignored invoices</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm
                ? 'No invoices match your search criteria'
                : 'Get started by adding a commercial invoice to the ignore list'}
            </p>
            {!searchTerm && (
              <button
                onClick={handleAdd}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
              >
                <Plus className="h-4 w-4" />
                Add First Invoice
              </button>
            )}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-blue-800">How this works</h4>
            <p className="mt-1 text-sm text-blue-700">
              Commercial invoices added to this list will be excluded from the cost allocation view and reports.
              This is useful for filtering out test data, cancelled orders, or duplicate entries that would skew your cost analysis.
              The ignore list is stored in BigQuery and is applied automatically when generating cost reports.
            </p>
          </div>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <IgnoreListForm
          record={editingRecord}
          onSave={handleSave}
          onCancel={() => {
            setShowForm(false);
            setEditingRecord(null);
          }}
          isEditing={!!editingRecord}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Remove from Ignore List?</h3>
              </div>
              <p className="text-gray-600 mb-2">
                Are you sure you want to remove this invoice from the ignore list?
              </p>
              <p className="text-sm text-gray-500 mb-6">
                <strong>Invoice:</strong> {deleteConfirm.commercial_invoice}<br />
                <strong>Reason:</strong> {REASON_LABELS[deleteConfirm.reason] || deleteConfirm.reason}
              </p>
              <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg mb-6">
                This invoice will start appearing in cost allocation reports again.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
